import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createVNode as _createVNode, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "cx-date-range-picker" }

import { ref, computed, ComputedRef, onMounted } from 'vue';
import VueDatePicker, { DatePickerMarker, DatePickerInstance } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import datetimehelper from '@/utils/datetimehelper';
import { isNil } from 'lodash';
import { endOfDay, startOfDay, subHours, subDays } from 'date-fns';
import CxDatePickerActivator from './CxDatePickerActivator.vue';
import CxMonthCell from './CxMonthCell.vue';
import CxYearCell from './CxYearCell.vue';
import { $gettext } from '@/translations/gettext.setup';

type DateRange = [Date, Date];

interface PresetRange {
  label: string,
  range: DateRange,
}

interface Props {
  modelValue: DateRange,
  minDate?: Date,
  maxDate?: Date
  events?: Date[],
  showActionPanel?: boolean,
  dateFormat?: string,
  disabled?: boolean,
  hideOnClickOutside?: boolean,
  loading?: boolean,
  placeholder?: string,
  label?: string,
  allowFutureDates?: boolean,
  freezeTo?: Date | null,
  freezeFrom?: Date | null
  quickSelect?: PresetRange[],
  showQuickSelectTab?: boolean
}



export default /*#__PURE__*/_defineComponent({
  __name: 'CxDateRangePicker',
  props: {
    modelValue: { default: () => [new Date(), new Date()] },
    minDate: {},
    maxDate: {},
    events: { default: () => [] },
    showActionPanel: { type: Boolean, default: false },
    dateFormat: {},
    disabled: { type: Boolean, default: false },
    hideOnClickOutside: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
    placeholder: {},
    label: {},
    allowFutureDates: { type: Boolean, default: false },
    freezeTo: { default: null },
    freezeFrom: { default: null },
    quickSelect: { default: () => ([
    { label: $gettext('Last 24h'), range: [subHours(new Date(), 24), new Date()] },
    { label: $gettext('Last 48h'), range: [subHours(new Date(), 48), new Date()] },
    { label: $gettext('Last 3 days'), range: [subDays(new Date(), 3), new Date()] },
    { label: $gettext('Last 7 days'), range: [subDays(new Date(), 7), new Date()] },
    { label: $gettext('Last 30 days'), range: [subDays(new Date(), 3), new Date()] },
  ]) },
    showQuickSelectTab: { type: Boolean, default: false }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit }) {

const props = __props;





const datepicker = ref<DatePickerInstance>(null);

const innerDate = ref<DateRange>(props.modelValue);

const updateModelValue = (newValue: DateRange) => {
  const firstDate = startOfDay(newValue[0]);
  const secondDate = endOfDay(newValue[1]);
  innerDate.value = [firstDate, secondDate];
  emit('update:modelValue', innerDate.value);
};

const updateInnerModel = (newValue: DateRange) => {
  innerDate.value = newValue;
};

onMounted(() => {
  if (props.freezeTo) {
    innerDate.value[1] = props.freezeTo;
  }
  if (props.freezeFrom) {
    innerDate.value[0] = props.freezeFrom;
  }
});

const innerMaxDate = computed(() => {
  if (props.allowFutureDates) {
    return props.maxDate ? endOfDay(props.maxDate) : undefined;
  }
  return new Date();
});

const innerDateFormat = computed(() => {
  if (props.dateFormat) {
    return props.dateFormat;
  }
  return datetimehelper.LOCALE_DATE_FORMAT;
});

const dateFormatter = (dateToFormat: Date[]) => {
  const firstDate = datetimehelper.format(dateToFormat[0], innerDateFormat.value);
  const secondDate = isNil(dateToFormat[1]) ? '' : datetimehelper.format(dateToFormat[1], innerDateFormat.value);
  return `${firstDate} - ${secondDate}`;
};

const selectedMonth = ref<number>(innerDate.value[0].getMonth());
const selectedYear = ref<number>(innerDate.value[0].getFullYear());

const activeDays: ComputedRef<DatePickerMarker[]> = computed(() => props
  .events.map(event => ({ date: event, type: 'dot' })));

const isActiveMonth = (month: number) => {
  const activeMonths = activeDays.value
    .filter(a => new Date(a.date).getFullYear() === selectedYear.value)
    .map(a => new Date(a.date).getMonth());
  return activeMonths.includes(month);
};

const isActiveYear = (year: number) => {
  const activeYears = activeDays.value
    .map(a => new Date(a.date).getFullYear());
  return activeYears.includes(year);
};

const handleChangeMonthYear = ({ month, year }: { month: number, year: number }) => {
  selectedMonth.value = month;
  selectedYear.value = year;
};

const resetSelectedMonthYear = () => {
  selectedMonth.value = props.modelValue === null ? new Date().getMonth() : props.modelValue[0].getMonth();
  selectedYear.value = props.modelValue === null ? new Date().getFullYear() : props.modelValue[0].getFullYear();
};

const handleClickOutside = () => {
  if (props.hideOnClickOutside && datepicker.value) {
    datepicker.value.closeMenu();
  }
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(VueDatePicker), {
      id: "date-range-picker",
      ref_key: "datepicker",
      ref: datepicker,
      dark: true,
      preventMinMaxNavigation: true,
      modelValue: _ctx.modelValue,
      minDate: _ctx.minDate,
      maxDate: innerMaxDate.value,
      range: true,
      markers: activeDays.value,
      autoApply: _ctx.showActionPanel,
      format: dateFormatter,
      clearable: false,
      disabled: _ctx.disabled || _ctx.loading,
      enableTimePicker: false,
      fixedEnd: _ctx.freezeTo !== null,
      fixedStart: _ctx.freezeFrom !== null,
      onClickOutside: handleClickOutside,
      presetRanges: _ctx.showQuickSelectTab ? _ctx.quickSelect : undefined,
      onUpdateMonthYear: handleChangeMonthYear,
      "onUpdate:modelValue": updateModelValue,
      onInternalModelChange: updateInnerModel,
      onOpen: resetSelectedMonthYear
    }, {
      "dp-input": _withCtx(({ value }) => [
        _renderSlot(_ctx.$slots, "activator", { value: value }, () => [
          _createVNode(CxDatePickerActivator, {
            value: value,
            disabled: _ctx.disabled,
            loading: _ctx.loading,
            placeholder: _ctx.placeholder,
            label: _ctx.label
          }, null, 8, ["value", "disabled", "loading", "placeholder", "label"])
        ])
      ]),
      "month-overlay-value": _withCtx(({ value, text }) => [
        _createVNode(CxMonthCell, {
          monthText: text,
          monthValue: value,
          isActiveMonth: isActiveMonth(value),
          selectedYear: selectedYear.value
        }, null, 8, ["monthText", "monthValue", "isActiveMonth", "selectedYear"])
      ]),
      "year-overlay-value": _withCtx(({ value }) => [
        _createVNode(CxYearCell, {
          yearValue: value,
          isActiveYear: isActiveYear(value)
        }, null, 8, ["yearValue", "isActiveYear"])
      ]),
      _: 3
    }, 8, ["modelValue", "minDate", "maxDate", "markers", "autoApply", "disabled", "fixedEnd", "fixedStart", "presetRanges"])
  ]))
}
}

})